<template>
    <div class="container-fluid px-4 py-4" style="background:#f8fafc;min-height:100vh;">

        <div class="d-flex align-items-center justify-content-between flex-wrap pb-3 mb-4" style="gap:12px;border-bottom:1px solid #e9ecef;">
            <div>
                <h4 class="text-primary mb-1" style="font-size:21px;font-weight:600;letter-spacing:-0.3px;">Utility &rarr; Working Liquidity</h4>
                <p class="text-secondary mb-0" style="font-size:13px;">Internal M-Pesa liquidity for Dukakit's own paybill.</p>
            </div>
            <button
                class="btn btn-sm btn-light d-inline-flex align-items-center"
                style="gap:6px;border-radius:8px;font-size:12px;border:1px solid #e2e8f0;"
                :disabled="loading"
                @click="load"
            >
                <i class="bi bi-arrow-clockwise" :class="{ spin: loading }"></i> Refresh
            </button>
        </div>

        <div class="row" style="row-gap:20px;">
            <div class="col-12 col-md-3">
                <div class="bg-white border h-100" style="border-radius:14px;border-color:#e9ecef !important;padding:20px;">
                    <div class="text-secondary mb-1" style="font-size:12px;">Working Balance</div>
                    <div class="text-dark" style="font-size:22px;font-weight:700;">{{ formatMoney(status.working_balance) }}</div>
                </div>
            </div>
            <div class="col-12 col-md-3">
                <div class="bg-white border h-100" style="border-radius:14px;border-color:#e9ecef !important;padding:20px;">
                    <div class="text-secondary mb-1" style="font-size:12px;">Utility Balance</div>
                    <div class="text-dark" style="font-size:22px;font-weight:700;">{{ formatMoney(status.utility_balance) }}</div>
                </div>
            </div>
            <div class="col-12 col-md-3">
                <div class="bg-white border h-100" style="border-radius:14px;border-color:#e9ecef !important;padding:20px;">
                    <div class="text-secondary mb-1" style="font-size:12px;">Balance As Of</div>
                    <div class="text-dark" style="font-size:14px;font-weight:600;">{{ status.balance_as_of ? formatDate(status.balance_as_of) : 'Never refreshed' }}</div>
                </div>
            </div>
            <div class="col-12 col-md-3">
                <div class="bg-white border h-100" style="border-radius:14px;border-color:#e9ecef !important;padding:20px;">
                    <div class="text-secondary mb-1" style="font-size:12px;">Automatic Top-up</div>
                    <span class="badge pill" :class="status.live_transfer_enabled ? 'badge-primary' : 'badge-secondary'">
                        {{ status.live_transfer_enabled ? 'Enabled' : 'Disabled' }}
                    </span>
                    <div class="text-secondary mt-1" style="font-size:11px;">{{ status.command_id }}</div>
                </div>
            </div>
        </div>

        <div class="bg-white border mt-4" style="border-radius:14px;border-color:#e9ecef !important;padding:20px 24px;">
            <div class="text-dark mb-3" style="font-size:14px;font-weight:600;">Recent Transfers</div>

            <div v-if="loading" class="empty-state">
                <i class="bi bi-arrow-repeat spin"></i>
                <p class="mb-0 mt-2">Loading...</p>
            </div>

            <div v-else-if="!status.transfers || status.transfers.length === 0" class="empty-state">
                <i class="bi bi-arrow-left-right"></i>
                <p class="mb-0 mt-2">No liquidity transfers yet.</p>
            </div>

            <div v-else class="table-container">
                <table class="dp-table">
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th class="text-right">Shortfall</th>
                            <th class="text-right">Requested</th>
                            <th>Status</th>
                            <th>Failure Reason</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="t in status.transfers" :key="t.id" class="dp-row">
                            <td><span class="text-secondary" style="font-size:12px;">{{ formatDate(t.created_at) }}</span></td>
                            <td class="text-right"><span class="text-dark" style="font-size:13px;">{{ formatMoney(t.shortfall_amount) }}</span></td>
                            <td class="text-right"><span class="text-dark" style="font-size:13px;font-weight:600;">{{ formatMoney(t.requested_amount) }}</span></td>
                            <td><span class="badge pill" :class="statusBadge(t.status)">{{ t.status }}</span></td>
                            <td><span class="text-secondary" style="font-size:12px;">{{ t.failure_reason || '—' }}</span></td>
                            <td class="text-right">
                                <button
                                    v-if="t.status === 'failed'"
                                    class="btn btn-sm btn-dark"
                                    style="border-radius:8px;font-size:11px;"
                                    :disabled="retrying === t.id"
                                    @click="retry(t)"
                                >{{ retrying === t.id ? 'Retrying...' : 'Retry' }}</button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'DukakitLiquidityAdmin',

    data() {
        return {
            status: {},
            loading: false,
            retrying: null,
        };
    },

    mounted() {
        this.load();
    },

    methods: {
        load() {
            this.loading = true;
            axios.get('/api/dukakit-payments/liquidity/status')
                .then(({ data }) => { this.status = data; })
                .catch(() => { this.$toasts.push({ type: 'error', message: 'Could not load liquidity status' }); })
                .finally(() => this.loading = false);
        },

        retry(transfer) {
            this.retrying = transfer.id;
            axios.post(`/api/dukakit-payments/liquidity/transfers/${transfer.id}/retry`)
                .then(() => {
                    this.$toasts.push({ type: 'success', message: 'Retry initiated' });
                    this.load();
                })
                .catch(err => {
                    this.$toasts.push({ type: 'error', message: err.response?.data?.message || 'Retry failed' });
                })
                .finally(() => this.retrying = null);
        },

        statusBadge(status) {
            if (status === 'completed') return 'badge-primary';
            if (status === 'failed') return 'badge-danger';
            if (status === 'reconciliation_required') return 'badge-dark';
            return 'badge-secondary';
        },

        formatMoney(v) {
            if (v === null || v === undefined) return '—';
            return 'KSh ' + Number(v).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        formatDate(d) {
            return d ? new Date(d).toLocaleString() : '';
        },
    },
};
</script>

<style scoped>
.spin { display: inline-block; animation: spin 0.8s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    color: #94a3b8;
    font-size: 13px;
}
.empty-state i { font-size: 32px; margin-bottom: 4px; opacity: 0.6; }

.table-container { overflow-x: auto; }

.dp-table { width: 100%; border-collapse: collapse; }
.dp-table thead { border-bottom: 2px solid #e2e8f0; }
.dp-table th {
    padding: 12px 16px;
    text-align: left;
    font-size: 11px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    white-space: nowrap;
}
.dp-table td { padding: 14px 16px; }
.dp-row { border-bottom: 1px solid #f1f5f9; }
.dp-row:hover { background: #f8fafc; }

.badge.pill {
    font-size: 11px;
    font-weight: 600;
    padding: 3px 10px;
    border-radius: 20px;
    text-transform: capitalize;
}
</style>
